import { useEffect, useMemo, useRef, useState } from "react";
import {
  AppShell,
  ClipList,
  ClipListButton,
  ClipListMeta,
  ClipListTitle,
  Content,
  GlobalOverride,
  Hero,
  HeroActions,
  HeroLogo,
  HeroSky,
  HeroTitle,
  MapFrame,
  MapLabel,
  MapLayout,
  MapSection,
  Panel,
  PanelSection,
  DesktopSidebarActions,
  FooterCredit,
  SidebarColumn,
} from "./styles";

const LOGO_URL = `${process.env.PUBLIC_URL}/youngdemocrats_logo_white_transparent.png`;
const CTA_URL = "https://linktr.ee/DemocratsYNG";
const GEOJSON_URL = `${process.env.PUBLIC_URL}/lebanon/data/clips.geojson`;
const MAPLIBRE_JS = "https://unpkg.com/maplibre-gl@4.7.1/dist/maplibre-gl.js";
const MAPLIBRE_CSS = "https://unpkg.com/maplibre-gl@4.7.1/dist/maplibre-gl.css";
const DEFAULT_SELECTED_CLIP_ID = "clip_8afa86cd-225d-41c0-9269-ae4a00c25671_689-726";

type ClipFeature = {
  type: "Feature";
  geometry: { type: "Point"; coordinates: [number, number] };
  properties: {
    clipId: string;
    title: string;
    clipAudio: string;
    durationSeconds: number;
    locationGeneral: string;
  };
};

type ClipCollection = {
  type: "FeatureCollection";
  metadata: {
    center: [number, number];
    zoom: number;
    maxBounds: [[number, number], [number, number]];
  };
  features: ClipFeature[];
};

declare global {
  interface Window {
    maplibregl?: any;
    dataLayer?: unknown[];
    gtag?: (...args: any[]) => void;
  }
}

function trackEvent(
  eventName: string,
  params: Record<string, string | number | boolean>
) {
  if (typeof window.gtag !== "function") {
    return;
  }

  window.gtag("event", eventName, params);
}

function useMapLibreAssets() {
  const [ready, setReady] = useState(Boolean(window.maplibregl));

  useEffect(() => {
    if (window.maplibregl) {
      setReady(true);
      return;
    }

    const existingCss = document.querySelector(`link[data-maplibre="true"]`);
    if (!existingCss) {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = MAPLIBRE_CSS;
      link.dataset.maplibre = "true";
      document.head.appendChild(link);
    }

    const script = document.createElement("script");
    script.src = MAPLIBRE_JS;
    script.async = true;
    script.onload = () => setReady(true);
    document.body.appendChild(script);

    return () => {
      script.remove();
    };
  }, []);

  return ready;
}

function formatDuration(seconds: number) {
  const minutes = Math.floor(seconds / 60);
  const remaining = Math.round(seconds % 60)
    .toString()
    .padStart(2, "0");
  return `${minutes}:${remaining}`;
}

const EMPTY_DATA: ClipCollection = {
  type: "FeatureCollection",
  metadata: {
    center: [35.44, 33.24],
    zoom: 9.7,
    maxBounds: [
      [35.08, 32.95],
      [35.74, 33.64],
    ],
  },
  features: [],
};

const CLIP_TITLE_REWRITE: Record<string, string> = {
  "מלכודת ללא מוצא - שגרת הרוגים וקריאה לצאת":
    "בופור: ככה נתקעים בבוץ ומאבדים אנשים",
  "ריחן - מיקום, מוצבי המשנה ומשימת התגבור":
    "ריחן: זה נשמע כמו תגבור, זה מרגיש כמו מלכודת",
  "ריחן - המערב הפרוע, טילים, מטענים ודרך מסוכנת":
    "ריחן: הדרך לשם היא פשוט הימור על החיים",
  "זעקת האימהות - אלף הרוגים ומה התכלית": "אלף הרוגים, ובשביל מה בדיוק",
  "זעקת האימהות - למה לחכות לעוד הרוג או שניים":
    "כמה עוד צריכים למות עד שמבינים",
  "סוג'וד - התקפה ישירה ומחיר ההרוגים": "סוג'וד: מתקפה ישירה, ועוד שמות ברשימה",
  'ריחן - חיפזון, נגמ"שים לא ממוגנים ומטען':
    "ריחן: שולחים פנימה בלי מיגון ובלי זמן",
  "יקינטון - מטען, חילוץ ומוות בקשר": "יקינטון: מטען, חילוץ, ואז מוות בקשר",
  'דלעת - פצמ"רים, פחד וצחוק כשגרה': "דלעת: גם פחד וגם צחוקים, כי זה נהיה רגיל",
  "ריחן-ג'זין - שיירות, פתיחת ציר ומטען":
    "ריחן-ג'זין: עוד שיירה, עוד סיכוי לא לחזור",
  "שיירות, צירים ומוצבים כשגרה": "ככה נראית השגרה כשחיים על מארבים",
  'יקינטון - פצמ"רים ואבדות כחלק מהשגרה': 'יקינטון: גם אבדות הפכו שם ל"רגיל"',
  "כרמלה מנשה - האם יציאות הלילה הפכו לשגרה":
    "יציאות לילה שהפכו למשהו שאמורים לבלוע",
  "זרעית - כל יום נהרג לנו חייל": "זרעית: כל יום עוד חייל, ואף אחד לא עוצר",
  "זרעית - בתוך ביצה ולא צריכים להיהרג בשבילנו":
    "זרעית: להיתקע בביצה הזאת זה לא גורל",
  "שגרת המוצבים - מוצבים מבוטנים ושיירות כמטרה":
    "המוצבים ממוגנים, השיירות הן המטרה",
  "ריחן - אין דקה לנשום ומארב של 80 שעות": "ריחן: אין אוויר, אין שקט, אין סוף",
  "דלעת - סיור חפיפה עם נקודות מוות": "דלעת: גם החפיפה נראית כמו מסלול מוות",
  "בופור - נסיעה פסטורלית וטיל או מטען": "בופור: הנוף יפה עד שהטיל מגיע",
  "בופור - מוצב חשוך והפצצה בכניסה": "בופור: חושך במוצב ופיצוץ בכניסה",
  "שגרת המוצבים - שיירת אספקה, סרטים וספרים":
    "אספקה, סרטים, ספרים, ואז שוב מלחמה",
  "שגרת המוצבים - עייפות, שמירות ולא להירדם":
    "לא להירדם, לא להישבר, לא לשאול למה",
  "תצפית - עשר שעות בעמדה וניתוק מוחלט":
    "תצפית: עשר שעות לבד מול כלום ושום דבר",
  "שגרת המוצבים - קוננות ספיגה ימים בלי להוריד שכפץ":
    'ימים על שכפ"ץ כאילו הגוף לא נגמר',
  "דלעת - התקפת מוצב וריצה לעמדה": "דלעת: רצים לעמדה כי שוב מתחיל בלגן",
  "עמדות בצפון הרצועה - טיל בחרך העמדה והאזעקה":
    "צפון הרצועה: טיל נכנס ישר לחרך",
  "דלעת - אותה עמדה שבה נהרג חייל": "דלעת: לעמוד בדיוק איפה שמישהו נהרג לפניך",
  "ריחן-סוג'וד - כל נסיעה וכל מארב הם מתח":
    "ריחן-סוג'וד: כל תזוזה מרגישה כמו הימור",
  "יום ראשון בלבנון - מפקד שיירה וארמדה":
    "יום ראשון בלבנון וכבר זורקים אותך להוביל",
  "גזרה מזרחית - מטען ביום הראשון ושלושה הרוגים":
    "גזרה מזרחית: יום ראשון, מטען, שלושה הרוגים",
  "חיים על קו אדום - הגנה על יישובי הצפון, אבל אתה נייח":
    "שומרים על הצפון, אבל אתה פשוט מטרה",
  "חיים על קו אדום - שיירה מתבטלת וקצת פצמרים": 'שיירה מתבטלת, הפצמ"רים לא',
  "חיים על קו אדום - טיל לתוך המוצב והדיוק של הפצמרים":
    'דלעת: טיל פנימה ופצמ"רים שפשוט פוגעים',
  "חיים על קו אדום - תקועים במוצב והם זזים, מצלמים ומפרסמים":
    "אנחנו תקועים, הם זזים, מצלמים ומעלים",
  "חיים על קו אדום - מנכס לנטל ובכמה הרוגים נצא":
    "מתי נבין שהנכס הזה הוא פשוט נטל",
};

function rewriteClipTitle(title: string) {
  return CLIP_TITLE_REWRITE[title] ?? title;
}

function getDefaultSelectedClipId(features: ClipFeature[]) {
  return (
    features.find(
      (feature) => feature.properties.clipId === DEFAULT_SELECTED_CLIP_ID
    )?.properties.clipId ??
    features[0]?.properties.clipId ??
    null
  );
}

function getFitBounds(
  data: ClipCollection
): [[number, number], [number, number]] {
  if (data.features.length > 0) {
    const lngs = data.features.map(
      (feature) => feature.geometry.coordinates[0]
    );
    const lats = data.features.map(
      (feature) => feature.geometry.coordinates[1]
    );

    return [
      [Math.min(...lngs), Math.min(...lats)],
      [Math.max(...lngs), Math.max(...lats)],
    ];
  }

  return data.metadata.maxBounds;
}

function expandBounds(
  bounds: [[number, number], [number, number]]
): [[number, number], [number, number]] {
  const [[minLng, minLat], [maxLng, maxLat]] = bounds;
  const lngPadding = Math.max((maxLng - minLng) * 0.95, 0.28);
  const latPadding = Math.max((maxLat - minLat) * 1.4, 0.22);

  return [
    [minLng - lngPadding, minLat - latPadding],
    [maxLng + lngPadding, maxLat + latPadding],
  ];
}

function expandMaxBounds(
  bounds: [[number, number], [number, number]]
): [[number, number], [number, number]] {
  const [[minLng, minLat], [maxLng, maxLat]] = bounds;

  return [
    [minLng - 0.45, minLat - 0.35],
    [maxLng + 0.45, maxLat + 0.35],
  ];
}

export default function LebanonLandingPage() {
  const mapRef = useRef<HTMLDivElement | null>(null);
  const mapInstanceRef = useRef<any>(null);
  const popupRef = useRef<any>(null);
  const clipItemRefs = useRef<Record<string, HTMLLIElement | null>>({});
  const playedClipIdsRef = useRef<Set<string>>(new Set());
  const [data, setData] = useState<ClipCollection>(EMPTY_DATA);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const mapReady = useMapLibreAssets();
  const fitBounds = useMemo(() => getFitBounds(data), [data]);
  const overviewBounds = useMemo(() => expandBounds(fitBounds), [fitBounds]);
  const maxOverviewBounds = useMemo(
    () => expandMaxBounds(data.metadata.maxBounds),
    [data]
  );

  useEffect(() => {
    fetch(GEOJSON_URL)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Failed to load clips: ${response.status}`);
        }
        return response.json();
      })
      .then((json: ClipCollection) => {
        const rewritten: ClipCollection = {
          ...json,
          features: json.features.map((feature) => ({
            ...feature,
            properties: {
              ...feature.properties,
              title: rewriteClipTitle(feature.properties.title),
            },
          })),
        };

        setData(rewritten);
        setSelectedId(getDefaultSelectedClipId(rewritten.features));
      })
      .catch(() => {
        setData(EMPTY_DATA);
      });
  }, []);

  const selectedClip = useMemo(
    () =>
      data.features.find(
        (feature) => feature.properties.clipId === selectedId
      ) ?? null,
    [data, selectedId]
  );

  useEffect(() => {
    if (
      !mapReady ||
      !mapRef.current ||
      mapInstanceRef.current ||
      !window.maplibregl
    ) {
      return;
    }

    const map = new window.maplibregl.Map({
      container: mapRef.current,
      style: {
        version: 8,
        sources: {
          "osm-raster": {
            type: "raster",
            tiles: ["https://tile.openstreetmap.org/{z}/{x}/{y}.png"],
            tileSize: 256,
            attribution: "&copy; OpenStreetMap contributors",
          },
        },
        layers: [{ id: "osm-raster", type: "raster", source: "osm-raster" }],
      },
      maxBounds: maxOverviewBounds,
      cooperativeGestures: true,
    });

    map.addControl(new window.maplibregl.NavigationControl(), "top-left");

    map.on("load", () => {
      map.fitBounds(overviewBounds, {
        padding: { top: 56, right: 36, bottom: 36, left: 36 },
        duration: 0,
      });

      map.addSource("clips", { type: "geojson", data });

      map.addLayer({
        id: "clip-points",
        type: "circle",
        source: "clips",
        paint: {
          "circle-radius": ["interpolate", ["linear"], ["zoom"], 8, 6, 12, 10],
          "circle-color": "#7F9650",
          "circle-stroke-color": "#fff2f4",
          "circle-stroke-width": 3,
          "circle-opacity": 0.95,
        },
      });

      map.addLayer({
        id: "clip-highlight",
        type: "circle",
        source: "clips",
        filter: ["==", ["get", "clipId"], ""],
        paint: {
          "circle-radius": ["interpolate", ["linear"], ["zoom"], 8, 10, 12, 16],
          "circle-color": "rgba(127, 150, 80, 0.18)",
          "circle-stroke-color": "#2A38D7",
          "circle-stroke-width": 4,
        },
      });

      map.on("click", "clip-points", (event: any) => {
        const feature = event.features?.[0];
        if (feature) {
          setSelectedId(feature.properties.clipId);
        }
      });

      map.on("mouseenter", "clip-points", () => {
        map.getCanvas().style.cursor = "pointer";
      });

      map.on("mouseleave", "clip-points", () => {
        map.getCanvas().style.cursor = "";
      });
    });

    const resizeMap = () => {
      map.resize();
      map.fitBounds(overviewBounds, {
        padding: { top: 56, right: 36, bottom: 36, left: 36 },
        duration: 0,
      });
    };

    window.addEventListener("resize", resizeMap);
    mapInstanceRef.current = map;

    return () => {
      popupRef.current?.remove();
      window.removeEventListener("resize", resizeMap);
      map.remove();
      mapInstanceRef.current = null;
    };
  }, [data, mapReady, maxOverviewBounds, overviewBounds]);

  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map || !map.isStyleLoaded()) {
      return;
    }

    const source = map.getSource("clips");
    if (source) {
      source.setData(data);
    }

    map.fitBounds(overviewBounds, {
      padding: { top: 56, right: 36, bottom: 36, left: 36 },
      duration: 0,
    });
  }, [data, overviewBounds]);

  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map || !selectedClip) {
      return;
    }

    const isMobileViewport = window.innerWidth <= 960;

    if (map.getLayer("clip-highlight")) {
      map.setFilter("clip-highlight", [
        "==",
        ["get", "clipId"],
        selectedClip.properties.clipId,
      ]);
    }

    if (isMobileViewport) {
      map.easeTo({
        center: selectedClip.geometry.coordinates,
        offset: [0, 110],
        duration: 350,
        essential: true,
      });
    }

    popupRef.current?.remove();
    popupRef.current = new window.maplibregl.Popup({
      closeButton: false,
      closeOnMove: false,
      offset: 12,
      maxWidth: "340px",
      anchor: isMobileViewport ? "bottom" : undefined,
    }).setLngLat(selectedClip.geometry.coordinates);

    const popupNode = document.createElement("div");
    popupNode.style.margin = "0";
    popupNode.style.minWidth = "260px";
    popupNode.style.background = "#120003";
    popupNode.style.color = "#fff2f4";
    popupNode.style.padding = "10px 12px";
    popupNode.style.borderInlineStart = "6px solid #7F9650";
    popupNode.style.borderTop = "1px solid rgba(255,255,255,.08)";
    popupNode.style.borderRight = "1px solid rgba(255,255,255,.08)";
    popupNode.style.borderBottom = "1px solid rgba(255,255,255,.08)";
    popupNode.style.fontFamily = "Heebo,sans-serif";
    popupNode.style.direction = "rtl";
    popupNode.style.textAlign = "right";

    const title = document.createElement("p");
    title.textContent = selectedClip.properties.title;
    title.style.margin = "0 0 4px";
    title.style.fontWeight = "900";
    title.style.lineHeight = "1.15";

    const location = document.createElement("p");
    location.textContent = selectedClip.properties.locationGeneral;
    location.style.margin = "0 0 10px";
    location.style.color = "#f3d7de";
    location.style.fontWeight = "700";

    const controls = document.createElement("div");
    controls.style.display = "flex";
    controls.style.alignItems = "center";
    controls.style.gap = "8px";
    controls.style.direction = "ltr";

    const playButton = document.createElement("button");
    playButton.type = "button";
    playButton.setAttribute("aria-label", "נגן");
    playButton.style.width = "42px";
    playButton.style.height = "42px";
    playButton.style.display = "inline-flex";
    playButton.style.alignItems = "center";
    playButton.style.justifyContent = "center";
    playButton.style.background = "#2A38D7";
    playButton.style.color = "#fff";
    playButton.style.border = "2px solid #050505";
    playButton.style.lineHeight = "1";

    const playIcon = document.createElement("span");
    playIcon.style.display = "block";
    playIcon.style.width = "0";
    playIcon.style.height = "0";
    playIcon.style.borderTop = "8px solid transparent";
    playIcon.style.borderBottom = "8px solid transparent";
    playIcon.style.borderLeft = "13px solid #fff";
    playIcon.style.marginInlineStart = "3px";

    const pauseIcon = document.createElement("span");
    pauseIcon.style.display = "none";
    pauseIcon.style.width = "14px";
    pauseIcon.style.height = "16px";
    pauseIcon.style.background =
      "linear-gradient(90deg, #fff 0 5px, transparent 5px 9px, #fff 9px 14px)";

    playButton.append(playIcon, pauseIcon);

    const timePill = document.createElement("div");
    timePill.textContent = formatDuration(
      selectedClip.properties.durationSeconds
    );
    timePill.style.flex = "1";
    timePill.style.padding = "0.62rem 0.72rem";
    timePill.style.background = "rgba(73, 90, 43, 0.92)";
    timePill.style.borderRight = "5px solid #050505";
    timePill.style.color = "#fff";
    timePill.style.fontWeight = "900";
    timePill.style.fontSize = "0.92rem";
    timePill.style.lineHeight = "1";

    const audio = document.createElement("audio");
    audio.preload = "none";
    audio.src = `${process.env.PUBLIC_URL}${selectedClip.properties.clipAudio}`;
    const clipAnalytics = {
      clip_id: selectedClip.properties.clipId,
      clip_title: selectedClip.properties.title,
      clip_location: selectedClip.properties.locationGeneral,
      clip_duration_seconds: selectedClip.properties.durationSeconds,
    };

    const syncPlayerState = () => {
      playIcon.style.display = audio.paused ? "block" : "none";
      pauseIcon.style.display = audio.paused ? "none" : "block";
      const current = Number.isFinite(audio.currentTime)
        ? audio.currentTime
        : 0;
      const total =
        Number.isFinite(audio.duration) && audio.duration > 0
          ? audio.duration
          : selectedClip.properties.durationSeconds;
      timePill.textContent = `${formatDuration(current)} / ${formatDuration(
        total
      )}`;
    };

    playButton.addEventListener("click", () => {
      if (audio.paused) {
        void audio.play();
      } else {
        audio.pause();
      }
    });

    audio.addEventListener("play", syncPlayerState);
    audio.addEventListener("pause", syncPlayerState);
    audio.addEventListener("timeupdate", syncPlayerState);
    audio.addEventListener("loadedmetadata", syncPlayerState);
    audio.addEventListener("play", () => {
      trackEvent("recording_play", clipAnalytics);

      if (!playedClipIdsRef.current.has(selectedClip.properties.clipId)) {
        playedClipIdsRef.current.add(selectedClip.properties.clipId);
        trackEvent("recording_play_unique", {
          ...clipAnalytics,
          unique_recordings_played_so_far: playedClipIdsRef.current.size,
        });
      }
    });
    audio.addEventListener("pause", () => {
      trackEvent("recording_pause", {
        ...clipAnalytics,
        playback_position_seconds: Math.round(audio.currentTime),
      });
    });
    audio.addEventListener("ended", () => {
      trackEvent("recording_complete", clipAnalytics);
      audio.currentTime = 0;
      syncPlayerState();
    });

    syncPlayerState();

    controls.append(playButton, timePill);
    popupNode.append(title, location, controls, audio);

    popupRef.current.setDOMContent(popupNode).addTo(map);
  }, [selectedClip]);

  useEffect(() => {
    if (!selectedId) {
      return;
    }

    const target = clipItemRefs.current[selectedId];
    if (!target) {
      return;
    }

    target.scrollIntoView({
      block: "start",
      inline: "nearest",
      behavior: "smooth",
    });
  }, [selectedId]);

  return (
    <>
      <GlobalOverride />
      <AppShell>
        <Hero>
          <HeroSky />
          <Content>
            <HeroLogo alt="הדמוקרטים" src={LOGO_URL} />
            <HeroTitle>
              מי שזוכר את החללים,
              <br />
              לא חוזר לאותה מלחמה
              <span>
                העדויות מהמוצבים בלבנון. אותו מקום. אותה שגרה. אותו סיום.
              </span>
              <span>
                יום הזיכרון הוא תזכורת למחיר הכבד של רצועת הביטחון בלבנון.
                הדמוקרטים הצעירים מתנגדים לחזור על טעויות הדור
                הקודם, לחזור לאותו הבוץ ולאותה מלחמת נצח.
              </span>
            </HeroTitle>
              <HeroActions>
              <a
                href={CTA_URL}
                rel="noreferrer"
                target="_blank"
                onClick={() =>
                  trackEvent("cta_click", {
                    cta_location: "hero",
                    destination_url: CTA_URL,
                  })
                }
              >
                לחצו כאן כדי למנוע את ההרוגים הבאים
              </a>
            </HeroActions>
          </Content>
        </Hero>

        <MapSection>
          <MapLayout>
            <MapFrame ref={mapRef}>
              <MapLabel>
                <strong>דרום לבנון</strong>
              </MapLabel>
            </MapFrame>

            <SidebarColumn>
              <Panel>
                <PanelSection $scrollable>
                  <ClipList>
                    {data.features.map((feature) => (
                      <li
                        key={feature.properties.clipId}
                        ref={(node) => {
                          clipItemRefs.current[feature.properties.clipId] =
                            node;
                        }}
                      >
                        <ClipListButton
                          $active={feature.properties.clipId === selectedId}
                          onClick={() => {
                            trackEvent("recording_select", {
                              clip_id: feature.properties.clipId,
                              clip_title: feature.properties.title,
                              clip_location: feature.properties.locationGeneral,
                              clip_duration_seconds:
                                feature.properties.durationSeconds,
                            });
                            setSelectedId(feature.properties.clipId);
                          }}
                          type="button"
                        >
                          <ClipListTitle>
                            {feature.properties.title}
                          </ClipListTitle>
                          <ClipListMeta>
                            {feature.properties.locationGeneral} ·{" "}
                            {formatDuration(feature.properties.durationSeconds)}
                          </ClipListMeta>
                        </ClipListButton>
                      </li>
                    ))}
                  </ClipList>
                </PanelSection>
              </Panel>

              <DesktopSidebarActions>
                <a
                  href={CTA_URL}
                  rel="noreferrer"
                  target="_blank"
                  onClick={() =>
                    trackEvent("cta_click", {
                      cta_location: "sidebar",
                      destination_url: CTA_URL,
                    })
                  }
                >
                  לחצו כאן כדי למנוע את ההרוגים הבאים
                </a>
              </DesktopSidebarActions>
            </SidebarColumn>
          </MapLayout>
        </MapSection>

        <FooterCredit>
          *קרדיט: כאן 11, ארכיון רשות השידור וגלי צה״ל
        </FooterCredit>
      </AppShell>
    </>
  );
}
